import { useEffect, useState } from "react";
import { LOGO_VARIANTS } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Check, Sparkles, Wand2 } from "lucide-react";
import { toast } from "sonner";

const LogoPicker = () => {
  const [selected, setSelected] = useState(() => localStorage.getItem("dds_logo") || "ai_lotus");

  useEffect(() => {
    localStorage.setItem("dds_logo", selected);
  }, [selected]);

  const apply = () => {
    localStorage.setItem("dds_logo", selected);
    toast.success("Logo applied. Navbar & Footer updated.");
    setTimeout(() => (window.location.href = "/"), 700);
  };

  const entries = Object.entries(LOGO_VARIANTS);
  const official = entries.filter(([, v]) => v.label.startsWith("⭐"));
  const ai = entries.filter(([, v]) => v.label.startsWith("🎨"));
  const premium = entries.filter(([, v]) => v.label.startsWith("✨"));
  const rest = entries.filter(([, v]) => !v.label.startsWith("✨") && !v.label.startsWith("🎨") && !v.label.startsWith("⭐"));

  const renderCard = ([key, v]) => {
    const Cmp = v.Component;
    const active = selected === key;
    const isAI = v.label.startsWith("🎨");
    const isPremium = v.label.startsWith("✨");
    const isOfficial = v.label.startsWith("⭐");
    return (
      <button
        key={key}
        onClick={() => setSelected(key)}
        data-testid={`logo-option-${key}`}
        className={`text-left card-tactile p-6 flex flex-col items-start gap-4 relative overflow-hidden ${active ? "!border-[#C95B24] ring-2 ring-[#C95B24]/25" : ""}`}
      >
        {(isAI || isPremium || isOfficial) && (
          <span className={`absolute top-3 left-3 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-1 rounded-full ${isOfficial ? "bg-[#F4C36B] text-[#1B4332]" : isAI ? "bg-[#C95B24] text-[#FDFBF7]" : "bg-[#1B4332] text-[#F4C36B]"}`}>
            {isOfficial ? "⭐ Official" : isAI ? <><Wand2 className="w-3 h-3" /> AI Premium</> : <><Sparkles className="w-3 h-3" /> Premium</>}
          </span>
        )}
        {active && (
          <span className="absolute top-3 right-3 w-7 h-7 rounded-full bg-[#C95B24] text-white flex items-center justify-center">
            <Check className="w-4 h-4" />
          </span>
        )}
        <div className={`w-full flex items-center justify-center rounded-xl py-8 border border-[#1B4332]/10 mt-6 ${isAI ? "bg-[#FDFBF7]" : "bg-gradient-to-br from-[#FDFBF7] to-[#F4EFE6]"}`}>
          <Cmp size={140} />
        </div>
        <div className="flex items-center gap-3 pt-1">
          <Cmp size={38} />
          <div>
            <div className="font-serif-display text-lg text-[#1B4332] leading-tight">Dharmoday</div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#4A4A4A]">Seva Sansthan</div>
          </div>
        </div>
        <div className="text-sm">
          <div className="font-semibold text-[#1B4332]">{v.label.replace(/^[🎨✨] /, "")}</div>
          {v.meaning && <div className="text-xs text-[#4A4A4A] mt-1 leading-relaxed">{v.meaning}</div>}
        </div>
      </button>
    );
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16" data-testid="logo-picker-page">
      <div className="mb-10">
        <p className="text-xs uppercase tracking-[0.25em] text-[#C95B24] font-semibold mb-2">Choose Your Mark</p>
        <h1 className="font-serif-display text-4xl sm:text-5xl text-[#1B4332] mb-3">Pick the perfect logo</h1>
        <p className="text-[#4A4A4A] max-w-2xl">
          {ai.length} AI-designed premium logos on top, then classic vector marks. Tap any card, then apply — your choice syncs to the whole site.
        </p>
      </div>

      {official.length > 0 && (
        <>
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-[1px] bg-[#F4C36B]" />
            <div className="text-xs uppercase tracking-[0.25em] text-[#C95B24] font-semibold flex items-center gap-1">
              ⭐ Your Official Trust Emblem
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-14">
            {official.map(renderCard)}
          </div>
        </>
      )}

      {ai.length > 0 && (
        <>
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-[1px] bg-[#C95B24]" />
            <div className="text-xs uppercase tracking-[0.25em] text-[#C95B24] font-semibold flex items-center gap-1">
              <Wand2 className="w-3 h-3" /> AI-Designed Premium Logos
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-14">
            {ai.map(renderCard)}
          </div>
        </>
      )}

      {premium.length > 0 && (
        <>
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-[1px] bg-[#1B4332]/40" />
            <div className="text-xs uppercase tracking-[0.25em] text-[#1B4332]/70 font-semibold flex items-center gap-1">
              <Sparkles className="w-3 h-3" /> Vector Premium
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-14">
            {premium.map(renderCard)}
          </div>
        </>
      )}

      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-[1px] bg-[#1B4332]/40" />
        <div className="text-xs uppercase tracking-[0.25em] text-[#1B4332]/70 font-semibold">Classic Marks</div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {rest.map(renderCard)}
      </div>

      <div className="mt-10 sticky bottom-6 flex justify-end z-10">
        <Button onClick={apply} data-testid="logo-apply-btn" className="btn-saffron rounded-full px-8 py-6 text-base shadow-2xl">
          Apply this logo across the site
        </Button>
      </div>
    </div>
  );
};

export default LogoPicker;
